import React from 'react';
import { Filter, Store, Calendar } from 'lucide-react';

// ============================================================================
// Filtros compartilhados do Troco — loja + mês (Controle, Depósito, Confirmação)
// ============================================================================
// O período é por ANO-MÊS (competência). Filtra pelo campo de data relevante de
// CADA tela (data do troco ou data do depósito) — o caller informa qual campo em
// aplicarFiltroLojaMes.

export const filtroVazio = { loja: '', mes: '' };

// Competência corrente ('YYYY-MM').
// ⚠️ Data LOCAL, nunca toISOString(): o fuso do Brasil é UTC−3, então a partir das
// 21h do último dia do mês o UTC já virou — e a tela abriria no mês seguinte,
// vazia, na virada. Erro clássico e silencioso.
export const mesAtual = () => {
    const h = new Date();
    return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`;
};

// Estado inicial das abas de CONSULTA do Troco (Controle, Saldos, Conferência
// PDV): abrem na competência corrente, que é o que se olha no dia a dia — sem
// isso a tela nascia com a rede inteira desde junho e a leitura começava por uma
// rolagem.
//
// ⚠️ NÃO usar nas abas de FILA (Depósito e Confirmação de Depósito). Ali a lista
// é trabalho pendente, e filtrar pelo mês corrente esconderia justamente o
// atrasado — havia 4 trocos de junho aguardando depósito quando isto foi escrito
// (decisão Julia, 2026-07-28).
export const filtroMesAtual = () => ({ loja: '', mes: mesAtual() });

// Primeiro mês com lançamento de troco no sistema — o seletor não desce abaixo
// disso (não existe dado antes, e mês vazio só confunde quem opera).
export const MES_MINIMO = '2026-06';

// Extrai o 'YYYY-MM' de uma data 'DD/MM/YYYY' (formato que o backend devolve).
// Retorna '' se vazio/sem barra.
const mesDe = (dataBR) => {
    if (!dataBR || !dataBR.includes('/')) return '';
    const [, m, a] = dataBR.split('/');
    return `${a}-${m}`;
};

// True se a data (DD/MM/YYYY) cai no mês selecionado (YYYY-MM). Mês vazio = tudo.
export const noMes = (dataBR, mes) => !mes || mesDe(dataBR) === mes;

// A MESMA regra, para data em ISO ('YYYY-MM-DD').
//
// ⚠️ Existe porque as telas novas do Troco (Controle e Conferência, 2026-07-30)
// trabalham em ISO — formato que já ordena cronologicamente como texto. Passar
// uma data ISO para o `noMes` acima NÃO dá erro: o `mesDe` procura uma barra,
// não acha, devolve '' e o filtro esconde TUDO, calado. Por isso são duas
// funções com nomes diferentes, e não uma que tenta adivinhar o formato.
export const noMesISO = (dataISO, mes) => !mes || String(dataISO || '').slice(0, 7) === mes;

// Filtro puro por loja + mês sobre um campo de data (ex.: 'DATA_TROCO').
export const aplicarFiltroLojaMes = (rows, filtro, campoData) =>
    rows.filter(r =>
        (!filtro.loja || r.LOJA === filtro.loja) &&
        noMes(r[campoData], filtro.mes)
    );

const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '15px', outline: 'none' };

// Barra de filtros (loja + mês). `lojas` = lista do /api/troco/lojas.
// `lojaBloqueada` trava o seletor de loja (caso do Gerente preso à própria loja).
//
// `semMes` esconde o seletor de competência. Existe para a aba **Saldos**
// (2026-07-30): lá o período virou NÍVEL DA ÁRVORE (Loja → mm-aaaa → transações),
// e um filtro global de mês passaria a competir com ele — filtrar julho e depois
// abrir o nó "julho" é a mesma pergunta feita duas vezes.
export const FiltroLojaMes = ({ lojas, filtro, onChange, lojaBloqueada, lojaFixa, semMes }) => {
    const set = (campo, valor) => onChange({ ...filtro, [campo]: valor });
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px', marginBottom: '24px', backgroundColor: '#fff', padding: '16px 24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                <Filter size={18} /> <span style={{ fontWeight: '600', fontSize: '14px' }}>Filtros:</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                <Store size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                <select
                    value={filtro.loja}
                    onChange={(e) => set('loja', e.target.value)}
                    disabled={lojaBloqueada}
                    style={{ ...inputStyle, paddingLeft: '36px', backgroundColor: lojaBloqueada ? '#f1f5f9' : '#fff' }}
                >
                    <option value="">Todas as Lojas</option>
                    {lojaFixa && !lojas.find(l => (l.NOME_LOJA || l) === lojaFixa) && (
                        <option value={lojaFixa}>{lojaFixa}</option>
                    )}
                    {lojas?.map((l, i) => {
                        const nome = l.NOME_LOJA || l;
                        return <option key={i} value={nome}>{nome}</option>;
                    })}
                </select>
            </div>

            {!semMes && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Calendar size={16} color="#94a3b8" />
                    <input type="month" min={MES_MINIMO} value={filtro.mes} onChange={(e) => set('mes', e.target.value)} style={inputStyle} />
                    {filtro.mes && (
                        <button
                            onClick={() => set('mes', '')}
                            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
                        >limpar</button>
                    )}
                </div>
            )}
        </div>
    );
};
